<template>
  <div class="ebook" :class="{ 'ebook--dark': escuro }" :style="variaveis">
    <div class="ebook__card">
      <!-- Logo e cor do produtor: sem isso a pagina parece phishing para quem acabou
           de comprar e nunca viu a plataforma. -->
      <img
        v-if="branding.logoUrl"
        class="ebook__logo"
        :src="branding.logoUrl"
        alt="logo"
        @error="branding.logoUrl = null"
      />
      <img v-else class="ebook__logo" :src="logoPlataforma" alt="logo" />

      <!-- CARREGANDO -->
      <div v-if="carregando" class="ebook__loading">
        <SpinnerWave color="var(--ebook-primary)"></SpinnerWave>
        <p>Carregando seu material…</p>
      </div>

      <!-- LINK VALIDO -->
      <div v-else-if="valido">
        <h1 class="ebook__titulo">{{ produto }}</h1>
        <p class="ebook__aluno" v-if="aluno">
          {{ aluno }}<span v-if="emailMascarado"> · {{ emailMascarado }}</span>
        </p>

        <template v-if="arquivos.length">
          <div class="ebook__arquivos">
            <div v-for="arquivo in arquivos" :key="arquivo.learningObjectId" class="ebook__arquivo">
              <div class="ebook__arquivo-info">
                <span class="ebook__arquivo-titulo">{{ arquivo.title }}</span>
                <span class="ebook__arquivo-tamanho" v-if="arquivo.size">{{ tamanho(arquivo.size) }}</span>
              </div>
              <button class="ebook__botao" @click="baixar(arquivo)">Baixar PDF</button>
            </div>
          </div>

          <p class="ebook__nota">
            Guarde este e-mail: o link continua funcionando e você pode baixar novamente
            quando quiser.
          </p>
        </template>

        <!-- Producao provou que a API pode devolver valid:true com files VAZIO (compra
             com o toggle ligado num produto sem PDF acessivel — primeiro chamado real da
             feature). Sem este estado, a pagina renderizava titulo e comprador e nenhuma
             acao, que foi exatamente a reclamacao do usuario. -->
        <div v-else class="ebook__vazio">
          Este conteúdo ainda não está disponível para download
        </div>
      </div>

      <!-- FALHA DE REDE OU SERVIDOR — estado proprio. Sem ele a pagina cairia no ramo
           de 404 e acusaria o aluno de ter copiado o link errado quando a culpa e da
           conexao (ou de um 5xx). -->
      <div v-else-if="falhaDeConexao">
        <h1 class="ebook__titulo">Não foi possível carregar seu conteúdo</h1>
        <p class="ebook__mensagem">Verifique sua conexão e tente novamente.</p>
        <button class="ebook__botao" @click="carregar()">Tentar novamente</button>
      </div>

      <!-- LINK INVALIDO — revogado ou matricula inativa -->
      <div v-else>
        <h1 class="ebook__titulo">{{ tituloDoErro }}</h1>
        <p class="ebook__mensagem">{{ mensagemDoErro }}</p>

        <!-- Reenvio so faz sentido quando o token existe e perdeu validade (200 +
             valid:false). Em 404 nao ha para quem reenviar. -->
        <div v-if="podeReenviar">
          <button class="ebook__botao" :disabled="reenviando || !!esperaSegundos" @click="reenviar()">
            <template v-if="reenviando">Enviando…</template>
            <template v-else-if="esperaSegundos">Aguarde {{ esperaFormatada }}</template>
            <template v-else>Receber um novo link por e-mail</template>
          </button>

          <p v-if="avisoReenvio" class="ebook__aviso" :class="{ 'ebook__aviso--ok': reenvioOk }">
            {{ avisoReenvio }}
          </p>
        </div>
      </div>

      <div class="ebook__rodape">
        <span>{{ nomePlataforma }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import { EbookAccessService } from "@/services/ebookAccess.service";
import SpinnerWave from "@/components/SpinnerWave";

/** Fallbacks para quando a API nao devolve branding (404). */
const COR_PADRAO = "#0564b1";

export default {
  name: "EbookAccess",
  components: { SpinnerWave },

  data() {
    return {
      carregando: true,
      valido: false,
      falhaDeConexao: false,
      motivo: null,
      existe: false,
      produto: "",
      aluno: "",
      email: "",
      arquivos: [],
      branding: { logoUrl: null, primaryColor: COR_PADRAO, secondaryColor: null, darkMode: false },
      reenviando: false,
      reenvioOk: false,
      avisoReenvio: "",
      esperaSegundos: 0,
      esperaTimer: null,
      temaAnterior: null
    };
  },

  computed: {
    token() {
      return this.$route.params.token;
    },

    /**
     * `$appName` e `$appLogoSrc` sao atribuidos ao Vue.prototype dentro do mounted() do
     * root (main.js:111-112), que roda DEPOIS do render dos filhos. Numa pagina que e a
     * primeira coisa pintada, os dois chegam undefined e o Vue emite warning.
     * Lendo do env aqui o valor e deterministico.
     */
    nomePlataforma() {
      return process.env.VUE_APP_DOMAIN_TITLE;
    },

    /**
     * Fallback quando o produtor nao tem logo (ou a URL do CDN nao responde).
     * No tema escuro o logo padrao e escuro e fica ilegivel — o projeto ja mantem a
     * variante branca ao lado, com o mesmo nome + `-white`.
     */
    logoPlataforma() {
      const arquivo = process.env.VUE_APP_LOGO_FILE_NAME;

      if (this.escuro) {
        const branco = arquivo.replace(/(\.[a-z]+)$/i, "-white$1");
        try {
          return require(`@/assets/images/${branco}`);
        } catch (e) {
          // sem variante branca para este white label: segue com o logo padrao
        }
      }

      return require(`@/assets/images/${arquivo}`);
    },

    escuro() {
      return !!this.branding.darkMode;
    },

    variaveis() {
      return { "--ebook-primary": this.branding.primaryColor || COR_PADRAO };
    },

    /** Armadilha #16 do contrato: nunca exibir o e-mail inteiro numa pagina publica. */
    emailMascarado() {
      const valor = this.email || "";
      const partes = valor.split("@");
      if (partes.length !== 2 || !partes[0]) return "";
      return `${partes[0].slice(0, 2)}***@${partes[1]}`;
    },

    podeReenviar() {
      return this.existe;
    },

    tituloDoErro() {
      if (!this.existe) return "Link não encontrado";
      if (this.motivo === "enrollment_inactive") return "Seu acesso não está mais ativo";
      return "Este link não é mais válido";
    },

    mensagemDoErro() {
      if (!this.existe) {
        return "Confira se o endereço foi copiado por inteiro do e-mail da sua compra.";
      }
      if (this.motivo === "enrollment_inactive") {
        return "Entre em contato com quem vendeu o material para regularizar seu acesso.";
      }
      return "Podemos enviar um link novo para o e-mail que você usou na compra.";
    },

    esperaFormatada() {
      const min = Math.floor(this.esperaSegundos / 60);
      const seg = this.esperaSegundos % 60;
      if (!min) return `${seg}s`;
      return `${min}min${seg ? ` ${seg}s` : ""}`;
    }
  },

  async mounted() {
    // `$updateTheme` e global e persiste entre rotas. Guardamos o estado para restaurar
    // no beforeDestroy e nao deixar a cor do produtor vazando para outras telas.
    this.temaAnterior = {
      darkTheme: this.$theme.darkTheme,
      primary: this.$theme.primaryColor,
      secondary: this.$theme.secondaryColor
    };

    await this.carregar();
  },

  beforeDestroy() {
    if (this.esperaTimer) clearInterval(this.esperaTimer);
    if (this.temaAnterior) this.$updateTheme(this.temaAnterior);
  },

  methods: {
    async carregar() {
      this.carregando = true;
      this.falhaDeConexao = false;

      try {
        const { data } = await EbookAccessService.resolver(this.token);

        this.existe = true;
        this.valido = !!data.valid;
        this.motivo = data.reason;
        this.produto = data.productName || "Seu material";
        this.aluno = data.studentName || "";
        this.email = data.studentEmail || "";
        this.arquivos = data.files || [];

        if (data.branding) this.branding = { ...this.branding, ...data.branding };
        this.aplicarTema();
      } catch (error) {
        this.valido = false;

        if (error.message === "link-inexistente") {
          // 404 = token nunca existiu. Sem branding, sem reenvio: tela generica.
          this.existe = false;
        } else {
          // Rede fora, timeout ou 5xx: nao e problema do link, e o aluno pode tentar
          // de novo. Estado proprio, com botao de recarga.
          this.falhaDeConexao = true;
        }
      } finally {
        this.carregando = false;
      }
    },

    aplicarTema() {
      this.$updateTheme({
        darkTheme: !!this.branding.darkMode,
        primary: this.branding.primaryColor || COR_PADRAO,
        secondary: this.branding.secondaryColor || this.branding.primaryColor || COR_PADRAO
      });
    },

    tamanho(bytes) {
      // O contrato avisa: `size` esta em BYTES (armadilha #7).
      const mb = bytes / 1024 / 1024;
      if (mb >= 1) return `${mb.toFixed(1).replace(".", ",")} MB`;
      return `${Math.max(1, Math.round(bytes / 1024))} KB`;
    },

    baixar(arquivo) {
      // Navegacao direta: o navegador segue o 302 para o CDN sozinho e usa o proprio
      // gerenciador de download. O arquivo chega como {learningObjectId}.pdf nos dois
      // ramos (com e sem DRM), nunca com o nome do material — por isso nao prometemos
      // o nome bonito na interface.
      window.location.href = EbookAccessService.urlDeDownload(arquivo.downloadUrl);
    },

    async reenviar() {
      if (this.reenviando || this.esperaSegundos) return;

      this.reenviando = true;
      this.avisoReenvio = "";
      this.reenvioOk = false;

      try {
        await EbookAccessService.reenviar(this.token);

        this.reenvioOk = true;
        this.avisoReenvio = "Enviamos um novo link para o e-mail da sua compra.";
        this.iniciarEspera(300);
      } catch (error) {
        const throttle = /^aguarde-(\d+)$/.exec(error.message || "");

        if (throttle) {
          // Nao e erro. E a janela de 5 min, compartilhada com o reenvio pelo suporte:
          // pode aparecer sem o aluno ter pedido nada.
          const segundos = Number(throttle[1]);
          this.avisoReenvio = "Um link já foi enviado há pouco. Confira sua caixa de entrada e o spam.";
          this.iniciarEspera(segundos);
        } else if (error.message === "tente-novamente") {
          this.avisoReenvio = "Falha temporária no envio. Tente novamente agora.";
        } else {
          this.avisoReenvio = "Não foi possível enviar o link. Tente novamente em instantes.";
        }
      } finally {
        this.reenviando = false;
      }
    },

    iniciarEspera(segundos) {
      this.esperaSegundos = segundos;

      if (this.esperaTimer) clearInterval(this.esperaTimer);

      this.esperaTimer = setInterval(() => {
        this.esperaSegundos -= 1;
        if (this.esperaSegundos <= 0) {
          clearInterval(this.esperaTimer);
          this.esperaTimer = null;
        }
      }, 1000);
    }
  }
};
</script>

<style lang="scss" scoped>
.ebook {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: #f2f3f7;
  color: #1a1a1a;

  &--dark {
    background: #0d0d13;
    color: #f5f5f5;

    .ebook__card {
      background: #17171f;
    }

    .ebook__aluno,
    .ebook__nota,
    .ebook__arquivo-tamanho,
    .ebook__rodape {
      color: #9a9aa5;
    }

    .ebook__arquivo {
      border-color: #2a2a35;
    }
  }
}

.ebook__card {
  width: 100%;
  max-width: 520px;
  background: #fff;
  border-radius: 12px;
  padding: 40px 32px 24px;
  text-align: center;
  box-shadow: 0 2px 16px rgba(0, 0, 0, 0.08);
}

.ebook__logo {
  max-width: 180px;
  max-height: 48px;
  object-fit: contain;
  margin-bottom: 28px;
}

.ebook__loading p {
  font-size: 14px;
  color: #777;
  margin-top: 16px;
}

.ebook__titulo {
  font-size: 22px;
  font-weight: 600;
  line-height: 1.3;
  margin: 0 0 8px;
}

.ebook__aluno {
  font-size: 13px;
  color: #777;
  margin: 0 0 28px;
}

.ebook__mensagem {
  font-size: 14px;
  line-height: 1.5;
  margin: 0 0 24px;
}

.ebook__arquivos {
  text-align: left;
}

.ebook__arquivo {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 0;
  border-top: solid 1px #eceef3;

  &:last-child {
    border-bottom: solid 1px #eceef3;
  }
}

.ebook__arquivo-info {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.ebook__arquivo-titulo {
  font-size: 14px;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ebook__arquivo-tamanho {
  font-size: 12px;
  color: #999;
}

.ebook__botao {
  background: var(--ebook-primary);
  color: #fff;
  border: 0;
  border-radius: 6px;
  padding: 0 20px;
  height: 42px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;

  &:hover:not(:disabled) {
    filter: brightness(0.92);
  }

  &:disabled {
    opacity: 0.55;
    cursor: default;
  }
}

.ebook__nota {
  font-size: 12px;
  color: #999;
  line-height: 1.5;
  margin: 24px 0 0;
}

.ebook__vazio {
  border: 1px solid #eceef3;
  background: #f7f8fa;
  border-radius: 8px;
  padding: 24px 16px;
  font-size: 14px;
  color: #555;
  line-height: 1.5;
}

.ebook--dark .ebook__vazio {
  border-color: #2a2a35;
  background: #1d1d26;
  color: #c5c5cd;
}

.ebook__aviso {
  font-size: 13px;
  line-height: 1.5;
  margin: 16px 0 0;
  color: #a8722a;

  &--ok {
    color: #2e7d32;
  }
}

.ebook__rodape {
  margin-top: 32px;
  padding-top: 16px;
  border-top: solid 1px #eceef3;
  font-size: 11px;
  color: #b0b0b8;
}

@media (max-width: 480px) {
  .ebook__card {
    padding: 32px 20px 20px;
  }

  .ebook__arquivo {
    flex-direction: column;
    align-items: stretch;
  }

  .ebook__botao {
    width: 100%;
  }
}
</style>
